import axios, { type AxiosRequestConfig } from 'axios';
import { ApiError } from './api-errors';

/**
 * Default Base URL for C-Market Infra APIs
 * Can be overridden via environment variables in different environments
 */
const getInfraBaseUrl = (): string => {
  // Check browser environment
  if (typeof window !== 'undefined' && (window as any).process?.env?.VITE_INFRA_API_URL) {
    return (window as any).process.env.VITE_INFRA_API_URL;
  }
  // Check node environment (use globalThis so this stays type-safe when the
  // shared source is compiled under a browser tsconfig without node types —
  // mirrors the `(window as any).process` access above)
  const nodeProcess = (globalThis as any).process;
  if (typeof nodeProcess !== 'undefined' && nodeProcess.env?.INFRA_API_URL) {
    return nodeProcess.env.INFRA_API_URL;
  }
  return 'https://c-market-infra-production.up.railway.app';
};

const infraAxiosInstance = axios.create({
  baseURL: getInfraBaseUrl(),
  timeout: 30000,
});

/**
 * Custom mutator for C-Market Infra APIs
 * ✅ Supports binary responses (ArrayBuffer)
 * ✅ Allows config overrides
 */
export const infraApi = async <T>(
  config: AxiosRequestConfig & { url: string; method: string },
): Promise<T> => {
  try {
    // If url is /pdf/html, we likely need arraybuffer for PDF generation
    const isPdfRequest = config.url.includes('/pdf/');
    
    const finalConfig: AxiosRequestConfig = {
      ...config,
      ...(isPdfRequest && { responseType: 'arraybuffer' }),
    };

    const res = await infraAxiosInstance(finalConfig);
    return res.data as T;
  } catch (error) {
    throw ApiError.fromUnknown(error);
  }
};

export type ErrorType<Error> = ApiError;
export type BodyType<BodyData> = BodyData;
